import React, { useRef, useState } from "react";
import { Alert, Badge, Button, Card, Form, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../../api/client";
import { tenantBrandingKey } from "../../../api/hooks";
import { useAuth } from "../../../app/providers/AuthProvider";
import { useTenant } from "../../../app/providers/TenantProvider";
import { useStudentCycles } from "../../student/hooks/useStudentCycles";
import { CycleTimeline } from "../../student/components/CycleTimeline";
import { useSaasSubscription } from "../../plan/hooks/useSaasSubscription";
import { InlineEditText } from "../../../components/InlineEditText";

function useMyLevel(enabled) {
  return useQuery({
    queryKey: ["student", "my-level"],
    queryFn: () =>
      apiClient.get("/api/v1/me/student/level/").then((r) => r.data),
    enabled,
  });
}

// Authenticated company detail — includes identification (NIT), not exposed on
// the public/anonymous branding endpoint used by TenantProvider.
function useMyCompany() {
  return useQuery({
    queryKey: ["my-company"],
    queryFn: () => apiClient.get("/api/v1/companies/me/").then((r) => r.data),
  });
}

const ROLE_LABELS = {
  OWNER: "Propietario",
  TEACHER: "Docente",
  STUDENT: "Estudiante",
};

function InfoRow({ label, value, children }) {
  return (
    <div className="d-flex justify-content-between align-items-center border-bottom py-2">
      <span className="text-muted">{label}</span>
      {children ?? <span className="fw-medium text-end">{value || "—"}</span>}
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (next !== confirm) {
      setError("Las contraseñas nuevas no coinciden.");
      return;
    }
    if (next.length < 8) {
      setError("La nueva contraseña debe tener al menos 8 caracteres.");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/api/v1/users/change-password/", {
        current_password: current,
        new_password: next,
      });
      toast.success("Contraseña actualizada.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      const data = err?.response?.data;
      setError(
        data?.field_errors?.current_password?.[0] ??
          data?.field_errors?.new_password?.[0] ??
          data?.current_password?.[0] ??
          data?.new_password?.[0] ??
          data?.detail ??
          "No se pudo cambiar la contraseña."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <Card.Header>
        <strong>Cambiar contraseña</strong>
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="danger" className="py-2">
            {error}
          </Alert>
        )}
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-medium">Contraseña actual</Form.Label>
            <Form.Control
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="fw-medium">Nueva contraseña</Form.Label>
            <Form.Control
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </Form.Group>
          <Form.Group className="mb-4">
            <Form.Label className="fw-medium">
              Confirmar nueva contraseña
            </Form.Label>
            <Form.Control
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </Form.Group>
          <Button
            type="submit"
            variant="primary"
            style={{ minHeight: 44 }}
            disabled={submitting}
          >
            {submitting ? <Spinner animation="border" size="sm" /> : "Guardar"}
          </Button>
        </Form>
      </Card.Body>
    </Card>
  );
}

const PLAN_STATUS_LABEL = {
  TRIAL: "Prueba",
  ACTIVE: "Activo",
  PAST_DUE: "Vencido",
  CANCELED: "Cancelado",
};

const PLAN_STATUS_VARIANT = {
  TRIAL: "info",
  ACTIVE: "success",
  PAST_DUE: "warning",
  CANCELED: "secondary",
};

// Full days from now until an ISO datetime (0 if already past).
function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
}

// Owner-only: current SaaS plan + a link to the full Plan page (upgrade,
// capacity blocks, invoices). The hook hits an IsOwner endpoint, so this must
// only mount for owners.
function PlanSummaryCard() {
  const { slug } = useTenant();
  const { data: subscription, isLoading } = useSaasSubscription();

  const status = subscription?.status;
  const trialDaysLeft =
    status === "TRIAL" ? daysUntil(subscription?.trial_ends_at) : null;

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <strong>Mi plan</strong>
        <Link to={`/${slug}/app/plan`} className="small">
          Ver detalles
        </Link>
      </Card.Header>
      <Card.Body>
        {isLoading ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <>
            <div className="d-flex align-items-center justify-content-between">
              <span className="fw-medium">
                {subscription?.plan?.name ?? "Sin plan"}
              </span>
              {status && (
                <Badge bg={PLAN_STATUS_VARIANT[status] ?? "secondary"}>
                  {PLAN_STATUS_LABEL[status] ?? status}
                </Badge>
              )}
            </div>
            {trialDaysLeft != null && (
              <div className="text-muted small mt-2">
                {trialDaysLeft > 0
                  ? `Prueba gratis: ${trialDaysLeft} día${
                      trialDaysLeft === 1 ? "" : "s"
                    } restante${trialDaysLeft === 1 ? "" : "s"}.`
                  : "Tu período de prueba terminó."}
              </div>
            )}
            {status === "PAST_DUE" && (
              <div className="text-muted small mt-2">
                Tu período de prueba terminó. Contáctanos para activar tu plan.
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

// Student-only: level + study cycle progress. Lives in the profile now that
// it's a small amount of info. The hooks hit IsStudent endpoints, so this must
// only mount for students.
function StudentProgressCard() {
  const { data: cycles, isLoading } = useStudentCycles();
  const { data: levelData } = useMyLevel(true);
  const levelName = levelData?.level?.name ?? "Sin asignar";

  return (
    <Card className="mb-4">
      <Card.Header>
        <strong>Mi progreso</strong>
      </Card.Header>
      <Card.Body>
        {isLoading ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <CycleTimeline
            cycles={cycles ?? { total_cycles: 0, total_months: 0, history: [] }}
            leadingStat={{ value: levelName, label: "Nivel" }}
          />
        )}
      </Card.Body>
    </Card>
  );
}

// Logo/imagen personalizada de la academia (solo OWNER). Se muestra discreto
// en el navbar (Navbar.js) para todos los miembros — docentes y estudiantes
// incluidos — vía branding.logo_url (endpoint público, sin auth). Si nunca
// se sube nada, ese espacio del navbar simplemente no aparece.
function AcademyLogoCard({ company }) {
  const { slug } = useTenant();
  const qc = useQueryClient();
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);

  const uploadMutation = useMutation({
    mutationFn: (file) => {
      const form = new FormData();
      form.append("logo", file);
      return apiClient
        .patch(`/api/v1/companies/${company.id}/`, form, {
          // apiClient fija "Content-Type: application/json" por defecto
          // (src/api/client.js) para todo el resto de la app. Con FormData
          // eso rompe el multipart: el adapter fetch de axios solo le quita
          // el Content-Type a una petición si YA es "multipart/form-data"
          // sin boundary — nunca si el default es "application/json", así
          // que el body sale con el boundary correcto pero el header dice
          // JSON, y el backend responde "no era un archivo". `undefined`
          // aquí deja que el navegador ponga el multipart/boundary real.
          headers: { "Content-Type": undefined },
        })
        .then((r) => r.data);
    },
    onSuccess: async () => {
      toast.success("Logo actualizado.");
      // Espera el refetch antes de soltar el preview local (onSettled) — si
      // no, hay un parpadeo de vuelta al logo viejo mientras llega el nuevo.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["my-company"] }),
        qc.invalidateQueries({ queryKey: tenantBrandingKey(slug) }),
      ]);
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.field_errors?.logo?.[0] ??
        err?.response?.data?.message ??
        "No se pudo actualizar el logo.";
      toast.error(msg);
    },
    onSettled: () => setPreview(null),
  });

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite volver a elegir el mismo archivo después
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    uploadMutation.mutate(file);
  }

  const currentUrl = preview ?? company?.logo_url;

  return (
    <Card className="mb-4">
      <Card.Header>
        <strong>Logo de la academia</strong>
      </Card.Header>
      <Card.Body className="d-flex align-items-center gap-3 flex-wrap">
        <div
          className="d-flex align-items-center justify-content-center flex-shrink-0"
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            overflow: "hidden",
            background: "var(--kp-body-bg)",
            border: "1px solid var(--kp-border)",
          }}
        >
          {currentUrl ? (
            <img
              src={currentUrl}
              alt="Logo de la academia"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <i
              className="mdi mdi-image-outline text-muted"
              style={{ fontSize: "1.75rem" }}
              aria-hidden="true"
            />
          )}
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="d-none"
            onChange={handleFileChange}
            aria-label="Subir logo de la academia"
          />
          <Button
            variant="outline-primary"
            size="sm"
            style={{ minHeight: 44 }}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
          >
            {uploadMutation.isPending ? (
              <Spinner size="sm" animation="border" aria-hidden="true" />
            ) : company?.logo_url ? (
              "Cambiar logo"
            ) : (
              "Subir logo"
            )}
          </Button>
          <div className="text-muted small mt-1">JPG, PNG o WEBP · máx. 2 MB</div>
        </div>
      </Card.Body>
    </Card>
  );
}

function ProfilePage() {
  const { user, updateUser } = useAuth();
  const { tenantName } = useTenant();
  const { data: company } = useMyCompany();

  const handleSaveName = async (name) => {
    const { data } = await apiClient.patch("/api/v1/users/me/", { name });
    updateUser({ name: data?.name ?? name });
    toast.success("Nombre actualizado.");
  };

  return (
    <>
      <Toaster position="top-right" />
      <div className="container-fluid py-3" style={{ maxWidth: 720 }}>
        <h4 className="mb-3">Mi perfil</h4>

        <Card className="mb-4">
          <Card.Header>
            <strong>Información de la cuenta</strong>
          </Card.Header>
          <Card.Body>
            <InfoRow label="Nombre">
              <InlineEditText value={user?.name} onSave={handleSaveName} />
            </InfoRow>
            <InfoRow label="Correo" value={user?.email} />
            <InfoRow
              label="Rol"
              value={ROLE_LABELS[user?.role] ?? user?.role}
            />
            <InfoRow
              label="Academia"
              value={company?.name ?? tenantName ?? user?.slug}
            />
            {/* El NIT es dato tributario: el backend solo lo envía al propietario. */}
            {company?.identification && (
              <InfoRow
                label="NIT / Identificación"
                value={company.identification}
              />
            )}
          </Card.Body>
        </Card>

        {user?.role === "OWNER" && company && <AcademyLogoCard company={company} />}
        {user?.role === "OWNER" && <PlanSummaryCard />}
        {user?.role === "STUDENT" && <StudentProgressCard />}

        <ChangePasswordCard />
      </div>
    </>
  );
}

export { ProfilePage };
